import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useGetPlatformRevenue, useListAllGyms } from "@workspace/api-client-react";
import { Building2, DollarSign, TrendingUp, Crown } from "lucide-react";
import { formatCurrency } from "@/lib/utils";

export default function SuperadminDashboard() {
  const { user } = useAuth();
  const { data: revenue } = useGetPlatformRevenue();
  const { data: gyms } = useListAllGyms();

  const stats = [
    { label: "Total Revenue", value: formatCurrency(revenue?.totalRevenue ?? 0), icon: DollarSign, color: "text-white" },
    { label: "Free Tier Revenue", value: formatCurrency(revenue?.freeRevenue ?? 0), icon: TrendingUp, color: "text-blue-400" },
    { label: "Premium Revenue", value: formatCurrency(revenue?.premiumRevenue ?? 0), icon: Crown, color: "text-primary" },
    { label: "Total Gyms", value: gyms?.length ?? 0, icon: Building2, color: "text-green-400" },
  ];

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Platform Overview</h1>
          <p className="text-muted-foreground text-lg">Welcome, {user?.firstName}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map(s => (
            <Card key={s.label} className="glass-panel border-white/5">
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">{s.label}</CardDescription>
                <CardTitle className={`text-3xl font-mono ${s.color}`}>{s.value}</CardTitle>
              </CardHeader>
              <CardContent><s.icon className={`w-5 h-5 ${s.color} opacity-50`} /></CardContent>
            </Card>
          ))}
        </div>

        <div>
          <h2 className="text-2xl font-display font-bold text-white mb-4">All Gyms</h2>
          <div className="space-y-3">
            {(gyms || []).map(gym => (
              <Card key={gym.id} className="glass-panel border-white/5 p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center text-xl font-bold text-white">{gym.name[0]}</div>
                    <div>
                      <h3 className="font-bold text-white text-lg">{gym.name}</h3>
                      <p className="text-sm text-muted-foreground">{gym.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-lg text-xs font-bold ${gym.platformPlan === "PREMIUM" ? "bg-primary/20 text-primary" : "bg-white/5 text-muted-foreground"}`}>
                      {gym.platformPlan}
                    </span>
                    <span className={`w-2.5 h-2.5 rounded-full ${gym.isActive ? "bg-green-400" : "bg-red-400"}`} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
